import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { Chart } from '@antv/g2';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../theme/ThemeContext';
import Select from '../../atoms/Select/Select';
import Segmented from '../../atoms/Segmented/Segmented';
import Button from '../../atoms/Button/Button';
import DownloadFile from '../../atoms/Icon/DownloadFile';
import ArrowRight from '../../atoms/Icon/ArrowRight';
import {
    BrandPriceLadderProps,
    BplPriceMetricFamily,
    BplSalesMetric,
    isNestedBplVizData,
} from './BrandPriceLadder.types';
import { ChartConfig, applyConfig } from '../../../utils/BaseChart/BaseChartOptions';
import { transformVizDataToBrandPriceLadder } from '../../../utils/transformVizData';
import {
    getAggregatedChartTooltipInteraction,
    getChartColumnHighlightInteraction,
} from '../../../utils/chartAggregatedTooltip';
import { formatNumWithKMB, formatNumWithKMBTwoDecimalPlaces, formatFixedSpacingXAxisLabel, CHART_FONT_FAMILY_CSS } from '../../../utils/formatters';
import BrandPriceLadderChartOptions from './BrandPriceLadderChartOptions';
import ChartFilterBar, { ChartFilterColumn, CHART_FILTER_SELECT_CLASSNAME } from '../EnhancedBarChart/ChartFilterBar';
import { chartMultiSelectTagRender } from '../EnhancedBarChart/chartFilterMultiTagRender';
import { chartFilterDropdownRender } from '../EnhancedBarChart/chartFilterDropdownRender';

const BPL_FIXED_BAR_WIDTH_PX = 32;
const BPL_FIXED_CATEGORY_GAP_PX = 72;
const BPL_FIXED_CATEGORY_STRIDE_PX = BPL_FIXED_BAR_WIDTH_PX + BPL_FIXED_CATEGORY_GAP_PX;
const BPL_X_SCALE_PADDING_RATIO = BPL_FIXED_CATEGORY_GAP_PX / BPL_FIXED_CATEGORY_STRIDE_PX;
const BPL_CHART_WRAPPER_HORIZONTAL_PADDING = 40;
const BPL_PRIMARY_AXIS_WIDTH_ESTIMATE = 80;
const BPL_SECONDARY_AXIS_WIDTH_ESTIMATE = 64;

const BPL_SALES_METRIC_OPTIONS: Array<{ label: string; value: BplSalesMetric }> = [
    { label: 'Volume', value: 'Volume' },
    { label: 'Value', value: 'Value' },
    { label: 'Units', value: 'Units' },
];

const BPL_PRICE_METRIC_FAMILY_SEGMENTED_OPTIONS: Array<{ label: string; value: BplPriceMetricFamily }> = [
    { label: 'PU', value: 'PPU' },
    { label: 'PEQ', value: 'PPEQ' },
];

const BrandPriceLadder: React.FC<BrandPriceLadderProps> = (props) => {
    const {
        vizData,
        title: propTitle,
        description,
        filters = [],
        footerLabel,
        showFooterArrow = true,
        onDownload,
        onBarClick,
        onPointClick,
        scrollbarVisibleCount: scrollbarVisibleCountProp,
        sizeAttributes,
        viewLevelAdditionalProps,
        additionalMarks,
        className = '',
        chartClassName = '',
    } = props;

    const scrollbarVisibleCount = scrollbarVisibleCountProp ?? 10;

    const theme = useTheme();
    const showNestedMetricDropdowns = isNestedBplVizData(vizData);
    const [priceMetricFamily, setPriceMetricFamily] = useState<BplPriceMetricFamily>('PPU');
    const [salesMetric, setSalesMetric] = useState<BplSalesMetric>('Value');

    useEffect(() => {
        setPriceMetricFamily('PPU');
        setSalesMetric('Value');
    }, [vizData]);

    // X-axis filter state (same logic as EnhancedBarChart)
    const [xFilter, setXFilter] = useState<string[]>([]);

    // X-axis filter options from vizData (unique x values, same as bar chart)
    const xFilterOptions = useMemo(() => {
        if (!vizData?.data) return [];
        const xValues = vizData.data.flatMap((d) => (Array.isArray(d.x) ? d.x : d.x != null ? [String(d.x)] : []));
        return Array.from(new Set(xValues)).sort();
    }, [vizData]);

    // Filtered vizData based on x-axis selection (same logic as bar chart)
    const filteredVizData = useMemo(() => {
        if (!vizData) return undefined;
        if (xFilter.length === 0) return vizData;
        const filteredData = vizData.data.filter((dataPoint) => {
            const xValue = Array.isArray(dataPoint.x) ? dataPoint.x?.[0] : dataPoint.x;
            const str = xValue != null ? String(xValue) : '';
            return str && xFilter.includes(str);
        });
        return { ...vizData, data: filteredData };
    }, [vizData, xFilter]);

    // Transform viz_data if provided (use filtered data)
    const chartConfig = useMemo(() => {
        if (!filteredVizData) {
            throw new Error('vizData is required for BrandPriceLadder');
        }
        return transformVizDataToBrandPriceLadder(filteredVizData, { priceMetricFamily, salesMetric });
    }, [filteredVizData, priceMetricFamily, salesMetric]);

    const { title, data, xAxisKey, salesBarConfig, pricePointConfigs, y1AxisLabel, y2AxisLabel } = chartConfig;

    const uniqueCategories = useMemo(
        () => Array.from(new Set(data.map((d: Record<string, unknown>) => d[xAxisKey]))),
        [data, xAxisKey],
    );
    const totalCategories = uniqueCategories.length;

    const [sliderRange, setSliderRange] = useState<[number, number]>([0, Math.max(0, totalCategories - 1)]);
    const [scrollOffset, setScrollOffset] = useState(0);
    const [chartWrapperWidth, setChartWrapperWidth] = useState(0);
    const chartWrapperRef = useRef<HTMLDivElement | null>(null);
    const scrollbarTrackRef = useRef<HTMLDivElement | null>(null);
    const touchStartRef = useRef<{ x: number; y: number } | null>(null);
    const scrollOffsetRef = useRef(0);
    const lastScrollTimeRef = useRef(0);
    const SCROLL_THROTTLE_MS = 150;

    useEffect(() => {
        setSliderRange([0, Math.max(0, totalCategories - 1)]);
        setScrollOffset(0);
        scrollOffsetRef.current = 0;
    }, [totalCategories]);

    const filteredCategoryCount = sliderRange[1] - sliderRange[0] + 1;
    const estimatedPlotWidth =
        chartWrapperWidth -
        BPL_CHART_WRAPPER_HORIZONTAL_PADDING -
        BPL_PRIMARY_AXIS_WIDTH_ESTIMATE -
        BPL_SECONDARY_AXIS_WIDTH_ESTIMATE;
    const effectiveScrollbarVisibleCount =
        estimatedPlotWidth > 0
            ? Math.max(1, Math.floor(estimatedPlotWidth / BPL_FIXED_CATEGORY_STRIDE_PX))
            : Math.max(1, scrollbarVisibleCount);
    const needsScroll = filteredCategoryCount > effectiveScrollbarVisibleCount;
    const maxScrollOffset = Math.max(0, filteredCategoryCount - effectiveScrollbarVisibleCount);
    const effectiveScrollOffset = Math.min(scrollOffset, maxScrollOffset);
    const visibleWindowStartIndex = needsScroll ? sliderRange[0] + effectiveScrollOffset : sliderRange[0];
    const visibleWindowEndIndex = needsScroll
        ? Math.min(visibleWindowStartIndex + effectiveScrollbarVisibleCount - 1, sliderRange[1])
        : sliderRange[1];
    const visibleWindowCategories = useMemo(
        () => new Set(uniqueCategories.slice(visibleWindowStartIndex, visibleWindowEndIndex + 1)),
        [uniqueCategories, visibleWindowStartIndex, visibleWindowEndIndex],
    );

    const scrollFilteredData = useMemo(
        () => data.filter((d) => visibleWindowCategories.has(d[xAxisKey])),
        [data, xAxisKey, visibleWindowCategories],
    );

    // Memoize all chart configurations
    const { barConfigs, pointConfigs, hasNegativeSales } = useMemo(() => {
        // Prepare full-domain data first so scrolling does not rescale either axis.
        const fullSalesData = data.map((item) => ({
            ...item,
            value: item.sales,
            metricName: salesBarConfig.label,
            configColor: salesBarConfig.color,
            axisGroup: 'y2',
            dataKey: salesBarConfig.dataKey,
        }));
        const visibleSalesData = scrollFilteredData.map((item) => ({
            ...item,
            value: item.sales,
            metricName: salesBarConfig.label,
            configColor: salesBarConfig.color,
            axisGroup: 'y2',
            dataKey: salesBarConfig.dataKey,
        }));

        const hasNegativeSales = fullSalesData.some(
            (d) => typeof d.value === 'number' && Number.isFinite(d.value) && d.value < 0,
        );

        const fullPriceData = data.flatMap((item) =>
            pricePointConfigs.map((config) => ({
                ...item,
                value: item[config.dataKey],
                metricName: config.label,
                configColor: config.color,
                axisGroup: 'y1',
                dataKey: config.dataKey,
            })),
        );
        // Prepare data for price points (on Y1 axis)
        const priceData = scrollFilteredData.flatMap((item) =>
            pricePointConfigs.map((config) => ({
                ...item,
                value: item[config.dataKey],
                metricName: config.label,
                configColor: config.color,
                axisGroup: 'y1',
                dataKey: config.dataKey,
            })),
        );

        // Calculate y min/max for both axes
        let y1MinMax: { min: number; max: number } | undefined;
        let y2MinMax: { min: number; max: number } | undefined;

        if (fullPriceData.length > 0) {
            const y1Values = fullPriceData.map((d) => d.value).filter((v) => v != null && !isNaN(v));
            if (y1Values.length > 0) {
                y1MinMax = {
                    min: Math.min(...y1Values),
                    max: Math.max(...y1Values),
                };
            }
        }

        if (fullSalesData.length > 0) {
            const y2Values = fullSalesData.map((d) => d.value).filter((v) => v != null && !isNaN(v));
            if (y2Values.length > 0) {
                const min = Math.min(...y2Values);
                const max = Math.max(...y2Values);
                const range = max - min || 1;
                const padding = Math.max(range * 0.05, Math.abs(max - min) * 0.05 || 1);
                y2MinMax = {
                    min: min - padding,
                    max: max + padding,
                };
            }
        }

        // Normalize Y2 values to Y1 scale for visual positioning
        let normalizedSalesData = visibleSalesData;
        if (y1MinMax && y2MinMax && fullPriceData.length > 0) {
            const y1Range = y1MinMax.max - y1MinMax.min || 1;
            const y2Range = y2MinMax.max - y2MinMax.min || 1;

            normalizedSalesData = visibleSalesData.map((d) => {
                if (d.value != null && !isNaN(d.value)) {
                    const normalized = ((d.value - y2MinMax!.min) / y2Range) * y1Range + y1MinMax!.min;
                    return {
                        ...d,
                        value: normalized,
                        originalValue: d.value,
                    };
                }
                return {
                    ...d,
                    originalValue: d.value,
                };
            });
        }

        // Create bar config for Sales (Y2 axis)
        const barConfigs: ChartConfig[] = [];
        const pointConfigs: ChartConfig[] = [];

        if (normalizedSalesData.length > 0) {
            const salesBarChartConfig: ChartConfig = {
                encode: {
                    x: xAxisKey,
                    y: 'value',
                },
                scale: {
                    color: { type: 'identity' },
                    x: { padding: BPL_X_SCALE_PADDING_RATIO },
                },
                style: {
                    fill: salesBarConfig.color,
                    maxWidth: BPL_FIXED_BAR_WIDTH_PX,
                    minWidth: BPL_FIXED_BAR_WIDTH_PX,
                    radiusTopLeft: 4,
                    radiusTopRight: 4,
                    radiusBottomLeft: 0,
                    radiusBottomRight: 0,
                },
                tooltip: {
                    title: (d: any) => String(d[xAxisKey] ?? ''),
                    items: [
                        (d: any) => ({
                            name: salesBarConfig.label,
                            value: formatNumWithKMB(Number(d.originalValue ?? d.value ?? 0)),
                            color: salesBarConfig.color,
                        }),
                    ],
                },
                labels: [
                    {
                        text: (d: any) => {
                            const val = d.originalValue !== undefined ? d.originalValue : d.value;
                            return val != null && !isNaN(val) && val >= 0
                                ? formatNumWithKMBTwoDecimalPlaces(val)
                                : '';
                        },
                        position: 'top',
                        textBaseline: 'bottom',
                        dy: -8,
                        fill: 'rgba(0, 0, 0, 0.65)',
                        fontSize: 12,
                        fontFamily: CHART_FONT_FAMILY_CSS,
                        transform: [{ type: 'overlapHide' }],
                    },
                    {
                        text: (d: any) => {
                            const val = d.originalValue !== undefined ? d.originalValue : d.value;
                            return val != null && !isNaN(val) && val < 0
                                ? formatNumWithKMBTwoDecimalPlaces(val)
                                : '';
                        },
                        position: 'bottom',
                        textBaseline: 'top',
                        dy: 8,
                        fill: 'rgba(0, 0, 0, 0.65)',
                        fontSize: 12,
                        fontFamily: CHART_FONT_FAMILY_CSS,
                        transform: [{ type: 'overlapHide' }],
                    },
                ],
                axis: {
                    x: {
                        title: xAxisKey,
                        size: 100,
                        // Fixed BPL geometry reserves `BPL_FIXED_CATEGORY_STRIDE_PX` per tick; size labels to
                        // that fixed space so every category stays visible (no autoHide) and never collides.
                        labelAutoWrap: false,
                        labelAutoHide: false,
                        labelAutoRotate: false,
                        labelFontSize: 13,
                        labelFontFamily: CHART_FONT_FAMILY_CSS,
                        labelStroke: '#000',
                        labelOpacity: 0.4,
                        labelFontWeight: 400,
                        labelFormatter: (text: string) =>
                            formatFixedSpacingXAxisLabel(text, BPL_FIXED_CATEGORY_STRIDE_PX),
                        ...(hasNegativeSales && {
                            crossPadding: 78,
                            labelSpacing: 12,
                        }),
                    },
                    y: {
                        position: 'left',
                        grid: true,
                        title: null,
                        gridStrokeOpacity: 0.2,
                        labelFormatter: (datum: any) => formatNumWithKMB(Number(datum)),
                        labelAutoRotate: false,
                        labelFontSize: 13,
                        labelFontFamily: CHART_FONT_FAMILY_CSS,
                        labelStroke: '#000',
                        labelOpacity: 0.4,
                        labelFontWeight: 400,
                    },
                },
                data: normalizedSalesData,
            };

            barConfigs.push(salesBarChartConfig);

            // Add Y2 axis mark for Sales (right axis) - this is just for the axis display
            if (y2MinMax) {
                const y2AxisConfig: ChartConfig = {
                    encode: {
                        x: xAxisKey,
                        y: 'originalValue',
                    },
                    scale: {
                        y: {
                            independent: true,
                            domain: [y2MinMax.min, y2MinMax.max],
                            nice: true,
                        },
                    },
                    style: {
                        fill: 'transparent',
                        stroke: 'transparent',
                        opacity: 0,
                    },
                    tooltip: false,
                    axis: {
                        y: {
                            position: 'right',
                            grid: false,
                            title: null,
                            gridStrokeOpacity: 0.2,
                            labelFormatter: (datum: any) => {
                                const num = Number(datum);
                                if (num === 0) return '0';
                                return formatNumWithKMB(num);
                            },
                            labelAutoRotate: false,
                            labelFontSize: 13,
                            labelFontFamily: CHART_FONT_FAMILY_CSS,
                            labelStroke: '#000',
                            labelOpacity: 0.4,
                            labelFontWeight: 400,
                        },
                    },
                    data: normalizedSalesData,
                };

                barConfigs.push(y2AxisConfig);
            }
        }

        // Create point configs for price metrics (Y1 axis)
        pricePointConfigs.forEach((config) => {
            const pointData = priceData.filter((d) => d.dataKey === config.dataKey);
            pointConfigs.push({
                encode: {
                    x: xAxisKey,
                    y: 'value',
                },
                scale: {
                    color: { type: 'identity' },
                },
                style: {
                    fill: config.color,
                    stroke: config.color,
                    r: 8,
                },
                axis: {
                    x: {
                        title: false,
                    },
                    y: {
                        title: false,
                        grid: false,
                    },
                },
                tooltip: {
                    title: (d: any) => String(d[xAxisKey] ?? ''),
                    items: [
                        (d: any) => ({
                            name: d.metricName || config.label,
                            value: formatNumWithKMB(Number(d.value)),
                            color: config.color,
                        }),
                    ],
                },
                data: pointData,
            });
        });

        return { barConfigs, pointConfigs, hasNegativeSales };
    }, [
        scrollFilteredData,
        data,
        xAxisKey,
        salesBarConfig,
        pricePointConfigs,
    ]);

    // Chart container ref
    const chartContainer = useRef<HTMLDivElement | null>(null);
    const chartInstance = useRef<Chart | null>(null);

    // Memoize chart options
    const chartOptions = useMemo(() => {
        const barIDs = barConfigs.map((_, index) => `bar${index + 1}`);
        const pointIDs = pointConfigs.map((_, index) => `point${index + 1}`);
        const options = new BrandPriceLadderChartOptions(scrollFilteredData, barIDs, pointIDs);

        // Set dimension attributes if available
        // insetRight reserves space for Y2 axis labels so they don't overlap with the bars
        // Do not set default height: G2's sizeOf() applies explicit height AFTER reading the
        // container, so a fixed height prevents the plot from matching available area (autoFit).
        // Pass height via sizeAttributes only when a fixed height is intentional.
        const defaultSizeAttributes = {
            paddingTop: 60,
            insetTop: 28,
            paddingBottom: 100 + 35,
            insetRight: 50,
            ...sizeAttributes,
        };
        options.setAttributes(defaultSizeAttributes);

        if (additionalMarks?.length) options.setChildren(additionalMarks);

        // Apply configurations for each bar
        barConfigs.forEach((barConfig, index) => {
            const barKey = `bar${index + 1}`;
            applyConfig(options, barConfig, barKey);
        });

        // Apply configurations for each point
        pointConfigs.forEach((pointConfig, index) => {
            const pointKey = `point${index + 1}`;
            applyConfig(options, pointConfig, pointKey);
        });

        // Set view-level additional props if available
        const defaultViewProps = {
            theme: { fontFamily: CHART_FONT_FAMILY_CSS },
            axis: {
                x: {
                    labelFormatter: (text: string) =>
                        formatFixedSpacingXAxisLabel(text, BPL_FIXED_CATEGORY_STRIDE_PX),
                    labelAutoHide: false,
                    labelAutoWrap: false,
                    labelAutoRotate: false,
                    title: false,
                    labelFontFamily: CHART_FONT_FAMILY_CSS,
                    ...(hasNegativeSales && {
                        crossPadding: 78,
                        labelSpacing: 12,
                    }),
                },
                y: {
                    labelFormatter: (value: number) => formatNumWithKMB(value),
                    title: false,
                    grid: true,
                    gridLineWidth: 1,
                    gridStroke: 'rgba(0, 0, 0, 0.06)',
                    labelFontFamily: CHART_FONT_FAMILY_CSS,
                },
            },
            legend: false,
            ...viewLevelAdditionalProps,
            interaction: {
                ...getChartColumnHighlightInteraction(),
                ...viewLevelAdditionalProps?.interaction,
                tooltip: {
                    ...getAggregatedChartTooltipInteraction(),
                    ...viewLevelAdditionalProps?.interaction?.tooltip,
                },
            },
            /**
             * G2 scrollbar/slider interactions can merge `clip: options.clip ?? true`, which clips
             * interval labels drawn above bars. Matches EnhancedBarChart / EnhancedStackedBarChart.
             */
            clip: false,
        };
        options.setViewAdditionalProps(defaultViewProps);

        return options.getOptions();
    }, [
        scrollFilteredData,
        barConfigs,
        pointConfigs,
        sizeAttributes,
        viewLevelAdditionalProps,
        additionalMarks,
        hasNegativeSales,
    ]);

    useEffect(() => {
        const wrapper = chartWrapperRef.current;
        if (!wrapper) return undefined;

        const updateWidth = () => {
            setChartWrapperWidth(wrapper.getBoundingClientRect().width);
        };
        updateWidth();

        if (typeof ResizeObserver === 'undefined') return undefined;

        const resizeObserver = new ResizeObserver(updateWidth);
        resizeObserver.observe(wrapper);
        return () => resizeObserver.disconnect();
    }, []);

    const scrollToOffset = useCallback(
        (targetOffset: number) => {
            if (!needsScroll) return;

            const next = Math.max(0, Math.min(maxScrollOffset, Math.round(targetOffset)));
            const prev = scrollOffsetRef.current;
            if (next === prev) return;
            scrollOffsetRef.current = next;
            setScrollOffset(next);
        },
        [needsScroll, maxScrollOffset],
    );

    const scrollChart = useCallback(
        (delta: number) => {
            scrollToOffset(scrollOffsetRef.current + delta);
        },
        [scrollToOffset],
    );

    const scrollbarThumbRatio = needsScroll
        ? Math.min(1, effectiveScrollbarVisibleCount / Math.max(1, filteredCategoryCount))
        : 1;
    const scrollbarThumbWidthPercent = scrollbarThumbRatio * 100;
    const scrollbarThumbLeftPercent =
        maxScrollOffset > 0 ? (effectiveScrollOffset / maxScrollOffset) * (100 - scrollbarThumbWidthPercent) : 0;

    const handleScrollbarPointer = useCallback(
        (clientX: number) => {
            const track = scrollbarTrackRef.current;
            if (!track || maxScrollOffset <= 0) return;
            const rect = track.getBoundingClientRect();
            const thumbWidthPx = rect.width * scrollbarThumbRatio;
            const availableTrackPx = Math.max(1, rect.width - thumbWidthPx);
            const leftPx = Math.max(0, Math.min(availableTrackPx, clientX - rect.left - thumbWidthPx / 2));
            const nextOffset = (leftPx / availableTrackPx) * maxScrollOffset;
            scrollToOffset(nextOffset);
        },
        [maxScrollOffset, scrollbarThumbRatio, scrollToOffset],
    );

    const handleScrollbarPointerDown = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            event.preventDefault();
            event.currentTarget.setPointerCapture?.(event.pointerId);
            handleScrollbarPointer(event.clientX);
        },
        [handleScrollbarPointer],
    );

    const handleScrollbarPointerMove = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            if ((event.buttons & 1) !== 1) return;
            handleScrollbarPointer(event.clientX);
        },
        [handleScrollbarPointer],
    );

    useEffect(() => {
        if (scrollOffsetRef.current > maxScrollOffset) {
            scrollOffsetRef.current = maxScrollOffset;
            setScrollOffset(maxScrollOffset);
        }
    }, [maxScrollOffset]);

    useEffect(() => {
        if (!needsScroll) return;
        const wrapper = chartWrapperRef.current;
        if (!wrapper) return;

        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            const now = Date.now();
            if (now - lastScrollTimeRef.current < SCROLL_THROTTLE_MS) return;
            lastScrollTimeRef.current = now;
            const rawDelta = e.deltaX || e.deltaY;
            const direction = rawDelta > 0 ? 1 : -1;
            scrollChart(direction);
        };

        const onTouchStart = (e: TouchEvent) => {
            if (e.touches.length === 1) {
                touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            }
        };

        const onTouchMove = (e: TouchEvent) => {
            if (!touchStartRef.current || e.touches.length !== 1) return;
            const dx = touchStartRef.current.x - e.touches[0].clientX;
            const dy = touchStartRef.current.y - e.touches[0].clientY;
            if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 8) {
                e.preventDefault();
                const now = Date.now();
                if (now - lastScrollTimeRef.current < SCROLL_THROTTLE_MS) return;
                lastScrollTimeRef.current = now;
                const direction = dx > 0 ? 1 : -1;
                scrollChart(direction);
                touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            }
        };

        const onTouchEnd = () => {
            touchStartRef.current = null;
        };

        wrapper.addEventListener('wheel', onWheel, { passive: false });
        wrapper.addEventListener('touchstart', onTouchStart, { passive: true });
        wrapper.addEventListener('touchmove', onTouchMove, { passive: false });
        wrapper.addEventListener('touchend', onTouchEnd, { passive: true });

        return () => {
            wrapper.removeEventListener('wheel', onWheel);
            wrapper.removeEventListener('touchstart', onTouchStart);
            wrapper.removeEventListener('touchmove', onTouchMove);
            wrapper.removeEventListener('touchend', onTouchEnd);
        };
    }, [needsScroll, scrollChart]);

    // Render chart
    useEffect(() => {
        if (chartContainer.current) {
            chartInstance.current = new Chart({ container: chartContainer.current });
            if (onBarClick) {
                chartInstance.current.on('interval:click', onBarClick);
            }
            if (onPointClick) {
                chartInstance.current.on('point:click', onPointClick);
            }
            chartInstance.current.options(chartOptions);
            chartInstance.current
                .render()
                .then(() => {
                    // Force layout recalculation after initial render to fix X-axis label wrap/ellipsis
                    chartInstance.current?.forceFit?.();
                })
                .catch((error) => console.error('Render failed', error));
        }

        return () => {
            if (chartInstance.current) {
                if (onBarClick) {
                    chartInstance.current.off('interval:click', onBarClick);
                }
                if (onPointClick) {
                    chartInstance.current.off('point:click', onPointClick);
                }
                chartInstance.current.destroy();
            }
        };
    }, [chartOptions, onBarClick, onPointClick]);

    // ResizeObserver: G2's autoFit only listens to window resize, not container resize.
    // When the right panel compresses (e.g. layout resize), forceFit() to track container width.
    useEffect(() => {
        const container = chartContainer.current;
        if (!container || typeof ResizeObserver === 'undefined') return;

        let rafId = 0;
        const resizeObserver = new ResizeObserver((entries) => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                const chart = chartInstance.current;
                if (!chart) return;
                const entry = entries[0];
                const measuredWidth = entry?.contentRect?.width ?? container.clientWidth;
                if (!measuredWidth) return;
                /**
                 * Use changeSize instead of forceFit because forceFit short-circuits when its
                 * cached _width matches sizeOf(...) — which can happen if the container's
                 * computed size is read mid-layout. changeSize unconditionally re-renders.
                 */
                const currentHeight = (chart.options() as { height?: number })?.height ?? 400;
                chart.changeSize?.(measuredWidth, currentHeight);
            });
        });
        resizeObserver.observe(container);
        return () => {
            cancelAnimationFrame(rafId);
            resizeObserver.disconnect();
        };
    }, []);

    // Build legend items
    const legendItems = [
        { label: salesBarConfig.label, color: salesBarConfig.color },
        ...pricePointConfigs.map((config) => ({ label: config.label, color: config.color })),
    ];

    const chartTheme = useMemo(
        () => ({
            ...theme,
            components: {
                ...(theme as { components?: { Select?: object; Segmented?: object } })?.components,
                Select: {
                    ...(theme as { components?: { Select?: object } })?.components?.Select,
                    colorTextPlaceholder: 'rgba(0,0,0,0.7)',
                },
                Segmented: {
                    ...(theme as { components?: { Segmented?: object } })?.components?.Segmented,
                    trackBg: '#F7F5FF',
                    itemSelectedBg: '#FFFFFF',
                    itemSelectedColor: '#44338a',
                    itemColor: 'rgba(0,0,0,0.45)',
                    itemHoverColor: '#44338a',
                    itemHoverBg: 'transparent',
                    trackPadding: 2,
                },
            },
        }),
        [theme],
    );

    return (
        <ConfigProvider theme={chartTheme}>
            <div
                className={`w-full bg-white rounded-lg border border-[rgba(0,0,0,0.06)] ${className}`}
                data-test={props['data-test']}
                data-test-id={props['data-test-id']}
                data-asper-brand-price-ladder
            >
                {/* Header Section */}
                <div className="p-5 border-b border-[rgba(0,0,0,0.06)]">
                    <div className="flex justify-between items-start mb-2">
                        <h3 className="font-['Open_Sans'] font-semibold text-[16px] leading-normal text-[rgba(0,0,0,0.88)] m-0">
                            {title || propTitle}
                        </h3>
                        <div className="flex items-end gap-2 shrink-0">
                            {showNestedMetricDropdowns && (
                                <>
                                    <div className="w-auto shrink-0">
                                        <ChartFilterColumn label="Sales">
                                            <Select
                                                value={salesMetric}
                                                onChange={(value) =>
                                                    setSalesMetric((value as BplSalesMetric) || 'Value')
                                                }
                                                options={BPL_SALES_METRIC_OPTIONS}
                                                className={`${CHART_FILTER_SELECT_CLASSNAME} !w-[104px]`}
                                                data-test-id={`${props['data-test-id']}-sales-metric`}
                                            />
                                        </ChartFilterColumn>
                                    </div>
                                    <div
                                        className="w-auto shrink-0 pb-1"
                                        data-test-id={`${props['data-test-id']}-price-metric-family`}
                                    >
                                        <div className="min-h-[22px] mb-[-4px]" aria-hidden="true" />
                                        <div className="min-w-0 mb-[-4px] flex h-[32px] items-center [&_.ant-segmented]:rounded-lg [&_.ant-segmented-item]:font-['Open_Sans'] [&_.ant-segmented-item]:text-[14px] [&_.ant-segmented-item-selected]:!font-semibold [&_.ant-segmented-item-selected]:!border [&_.ant-segmented-item-selected]:!border-[#44338a] [&_.ant-segmented-item-selected]:!shadow-none">
                                            <Segmented
                                                value={priceMetricFamily}
                                                onChange={(value) =>
                                                    setPriceMetricFamily(
                                                        (value as BplPriceMetricFamily) || 'PPU',
                                                    )
                                                }
                                                options={BPL_PRICE_METRIC_FAMILY_SEGMENTED_OPTIONS}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                            {onDownload && (
                                <Button
                                    type="text"
                                    icon={<DownloadFile size={16} />}
                                    onClick={onDownload}
                                    className="!p-2"
                                    data-test-id={`${props['data-test-id']}-download-btn`}
                                />
                            )}
                        </div>
                    </div>

                    {description && (
                        <p className="font-['Open_Sans'] font-normal text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] m-0">
                            {description}
                        </p>
                    )}
                </div>

                {/* Filters Section - same logic and fonts as EnhancedBarChart */}
                {(filters.length > 0 || (vizData && xFilterOptions.length > 0)) && (
                    <div className="px-5 pt-5 pb-3">
                        <ChartFilterBar>
                            {/* X-axis filter (auto-generated from vizData, same as bar chart) */}
                            {vizData && xFilterOptions.length > 0 && (
                                <ChartFilterColumn
                                    label={(() => {
                                        const rawTitle = vizData.xAxisTitle?.[0] ?? (vizData as any).xAxisTitles?.[0];
                                        if (rawTitle && String(rawTitle).toLowerCase() !== 'x-axis filter') {
                                            return rawTitle;
                                        }
                                        return 'X-Axis';
                                    })()}
                                >
                                    <Select
                                        mode="multiple"
                                        value={xFilter}
                                        onChange={(value) =>
                                            setXFilter(Array.isArray(value) ? value : value != null ? [value] : [])
                                        }
                                        options={xFilterOptions.map((x) => ({
                                            label: x,
                                            value: x,
                                        }))}
                                        tagRender={chartMultiSelectTagRender(xFilter, (v) => v)}
                                        className={CHART_FILTER_SELECT_CLASSNAME}
                                        placeholder="Select..."
                                        data-test-id={`${props['data-test-id']}-filter-x`}
                                        dropdownRender={(menu) =>
                                            chartFilterDropdownRender(menu, {
                                                onReset: () => setXFilter([]),
                                                onSelectAll: () => setXFilter([...xFilterOptions]),
                                                dataTestIdPrefix: `${props['data-test-id']}-filter-x`,
                                            })
                                        }
                                    />
                                </ChartFilterColumn>
                            )}
                            {/* Custom filters from props */}
                            {filters.map((filter, index) => (
                                <ChartFilterColumn key={`filter-${index}`} label={filter.label}>
                                    <Select
                                        value={filter.defaultValue}
                                        onChange={filter.onChange}
                                        options={filter.options}
                                        className={CHART_FILTER_SELECT_CLASSNAME}
                                        data-test-id={`${props['data-test-id']}-filter-${index}`}
                                    />
                                </ChartFilterColumn>
                            ))}
                        </ChartFilterBar>
                    </div>
                )}

                {/* Legend Section */}
                {legendItems.length > 0 && (
                    <div className="px-5 py-3 w-full min-w-0">
                        <div className="flex justify-center gap-4 flex-wrap items-center max-w-full">
                            {legendItems.map((item, index) => (
                                <div key={`legend-${index}`} className="flex items-center gap-2 min-w-0 max-w-full">
                                    <div
                                        className={`w-3 h-3 shrink-0 ${index === 0 ? 'rounded-sm' : 'rounded-full'}`}
                                        style={{ backgroundColor: item.color }}
                                    />
                                    <span className="font-['Open_Sans'] font-normal text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] break-words">
                                        {item.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Y-Axis Labels */}
                {(y1AxisLabel || y2AxisLabel) && (
                    <div className="px-5 pt-3">
                        <div className="flex justify-between">
                            {y1AxisLabel && (
                                <p className="font-['Open_Sans'] font-normal text-sm leading-[20px] text-[rgba(0,0,0,0.45)] m-0">
                                    {y1AxisLabel}
                                </p>
                            )}
                            {y2AxisLabel && (
                                <p className="font-['Open_Sans'] font-normal text-sm leading-[20px] text-[rgba(0,0,0,0.45)] m-0">
                                    {y2AxisLabel}
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* Chart Section — x-axis uses G2 scrollbar (categorical), with wheel/touch scroll like EnhancedBarChart */}
                <div ref={chartWrapperRef} className={`px-5 py-5 ${chartClassName}`}>
                    <div
                        ref={chartContainer}
                        className="w-full min-w-0"
                        data-test={`${props['data-test']}-chart`}
                        data-asper-brand-price-ladder-chart
                    />
                </div>

                {needsScroll && (
                    <div className="px-5 pb-4 flex justify-center" data-export-exclude>
                        <div
                            ref={scrollbarTrackRef}
                            role="scrollbar"
                            aria-orientation="horizontal"
                            aria-valuemin={0}
                            aria-valuemax={maxScrollOffset}
                            aria-valuenow={effectiveScrollOffset}
                            tabIndex={0}
                            className="relative h-2 rounded-full cursor-pointer select-none touch-none"
                            style={{
                                width: '38%',
                                minWidth: 112,
                                maxWidth: 360,
                                background: '#f0f0f0',
                            }}
                            onPointerDown={handleScrollbarPointerDown}
                            onPointerMove={handleScrollbarPointerMove}
                            onKeyDown={(event) => {
                                if (event.key === 'ArrowLeft') {
                                    event.preventDefault();
                                    scrollChart(-1);
                                } else if (event.key === 'ArrowRight') {
                                    event.preventDefault();
                                    scrollChart(1);
                                }
                            }}
                            data-test-id={`${props['data-test-id']}-v2-scroller`}
                        >
                            <div
                                className="absolute top-0 h-2 rounded-full"
                                style={{
                                    left: `${scrollbarThumbLeftPercent}%`,
                                    width: `${scrollbarThumbWidthPercent}%`,
                                    background: '#5B50D1',
                                    opacity: 0.45,
                                }}
                            />
                        </div>
                    </div>
                )}

                {/* Footer Section */}
                {footerLabel && (
                    <div className="px-5 pb-5 flex justify-center items-center gap-2">
                        <span className="font-['Open_Sans'] font-semibold text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)]">
                            {footerLabel}
                        </span>
                        {showFooterArrow && <ArrowRight size={16} color="rgba(0,0,0,0.88)" />}
                    </div>
                )}
            </div>
        </ConfigProvider>
    );
};

export default BrandPriceLadder;
